<template>
  <div class="checkbox-toggle" v-bind="$attrs">
    <input
      type="checkbox"
      ref="input"
      :id="id"
      :checked="checked"
      :disabled="$attrs.disabled"
      @change="($event) => change($event.target.checked)"
      :indeterminate.prop="indeterminate"
    />
    <label :for="id" @click.stop>
      <slot />
      <div
        class="checkbox-toggle__switch"
        :class="`checkbox-toggle__switch--${position}`"
        :data-checked="'\xa0'"
        :data-unchecked="'\xa0'"
      ></div>
    </label>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'CheckBoxToggle',
  emits: { 'update:checked': null },

  inheritAttrs: false,

  props: {
    checked: {
      type: Boolean,
      default: false,
      required: false,
    },

    id: {
      type: String,
      required: true,
    },

    /**
     * loose: toggle sits directly behind label text
     * end: align toggle at the end of container
     * start: align toggle at the start of container
     */
    position: {
      type: String as PropType<'loose' | 'end' | 'start'>,
      default: 'loose',
    },

    indeterminate: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    /**
     * @public
     */
    focus() {
      this.$refs.input.focus();
    },
    change(value) {
      this.$emit('update:checked', value);
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'sass:math';
@use 'sass:color';

// Adapated from https://codepen.io/personable/pen/NWLZrV

@mixin checkbox-toggle-branding(
  $checkbox-toggle-off-color: colors.$color-grey-300,
  $checkbox-toggle-on-color: colors.$color-blue-600,
  $checkbox-toggle-inactive-text: inherit,
  $checkbox-toggle-transition: cubic-bezier(0, 1, 0.5, 1)
) {
  input[type='checkbox'] {
    &[disabled] ~ label {
      color: rgba($checkbox-toggle-off-color, 0.5);
    }

    &:focus ~ label,
    &:hover ~ label {
      .checkbox-toggle__switch {
        background-color: $checkbox-toggle-off-color;
      }
    }

    &:checked {
      ~ label {
        .checkbox-toggle__switch {
          background-color: $checkbox-toggle-on-color;
        }
      }

      &:focus,
      &:hover {
        ~ label {
          .checkbox-toggle__switch {
            background-color: color.adjust($checkbox-toggle-on-color, $lightness: 5%);
          }
        }
      }
    }
  }

  label {
    .checkbox-toggle__switch {
      transition: background-color 0.3s $checkbox-toggle-transition;
      background: $checkbox-toggle-off-color;
      &:after {
        transition: transform 0.3s $checkbox-toggle-transition;
      }

      &--end {
        margin-left: auto;
      }

      &--start {
        order: -1;
      }
    }
  }
}

@mixin checkbox-toggle-appearance(
  $checkbox-toggle-width: 26px,
  $checkbox-toggle-height: 16px,
  $checkbox-toggle-border-radius: 60px,
  $checkbox-toggle-offset: 2px,
  $checkbox-toggle-label-font-size: inherit,
  $checkbox-toggle-switch-font-size: 12px,
  $checkbox-toggle-shadow: none
) {
  $checkbox-toggle-switch-width: calc($checkbox-toggle-width/2);

  input[type='checkbox'] {
    &:focus ~ label,
    &:hover ~ label {
      .can-toggle__switch {
        &:after {
          box-shadow: $checkbox-toggle-shadow;
        }
      }
    }

    &:checked {
      ~ label {
        .checkbox-toggle__switch {
          &:after {
            transform: translate3d(
              $checkbox-toggle-width - ($checkbox-toggle-switch-width + $checkbox-toggle-offset + 1),
              0,
              0
            );
          }
        }
      }

      &:focus,
      &:hover {
        ~ label {
          .can-toggle__switch {
            &:after {
              box-shadow: $checkbox-toggle-shadow;
            }
          }
        }
      }
    }

    &:indeterminate {
      ~ label {
        .checkbox-toggle__switch {
          &:after {
            transform: translate3d(
              math.div($checkbox-toggle-width, 2) - (math.ceil(math.div($checkbox-toggle-switch-width, 2)) + 1),
              0,
              0
            );
          }
        }
      }

      &:focus,
      &:hover {
        ~ label {
          .can-toggle__switch {
            &:after {
              box-shadow: $checkbox-toggle-shadow;
            }
          }
        }
      }
    }
  }

  label {
    font-size: $checkbox-toggle-label-font-size;

    .checkbox-toggle__switch {
      height: $checkbox-toggle-height;
      flex: 0 0 $checkbox-toggle-width;
      width: $checkbox-toggle-width;
      border-radius: $checkbox-toggle-border-radius;

      &:before {
        left: 0;
        font-size: $checkbox-toggle-switch-font-size;
        line-height: $checkbox-toggle-height;
        width: calc($checkbox-toggle-width/2);
        padding: 0 12px;
      }

      &:after {
        top: $checkbox-toggle-offset;
        left: $checkbox-toggle-offset;
        border-radius: calc($checkbox-toggle-border-radius/2);
        width: $checkbox-toggle-switch-width - $checkbox-toggle-offset + 1;
        line-height: $checkbox-toggle-height - ($checkbox-toggle-offset * 2);
        font-size: $checkbox-toggle-switch-font-size;
      }

      &:hover {
        &:after {
          box-shadow: $checkbox-toggle-shadow;
        }
      }
    }
  }
}

.checkbox-toggle {
  position: relative;
  display: flex;
  gap: 8px;
  align-items: center;
  overflow: hidden;
  height: 100%;
  width: 100%;

  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  input[type='checkbox'] {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;

    &[disabled] ~ label {
      pointer-events: none;
      .checkbox-toggle__switch {
        opacity: 0.4;
      }
    }

    &:checked {
      ~ label {
        .checkbox-toggle__switch {
          &:before {
            content: attr(data-unchecked);
            left: 0;
          }

          &:after {
            content: attr(data-checked);
          }
        }
      }
    }
  }

  label {
    cursor: pointer;
    position: relative;
    display: flex;
    gap: 8px;
    align-items: center;
    width: 100%;
    height: 100%;

    .checkbox-toggle__switch {
      position: relative;

      &:before {
        content: attr(data-checked);
        position: absolute;
        top: 0;
        text-transform: uppercase;
        text-align: center;
      }

      &:after {
        content: attr(data-unchecked);
        position: absolute;
        z-index: 5;
        text-transform: uppercase;
        text-align: center;
        background: white;
        transform: translate3d(0, 0, 0);
      }
    }
  }

  // Default values for .checkbox-toggle class
  @include checkbox-toggle-branding;
  @include checkbox-toggle-appearance;
}
</style>
